import React from 'react';
import { APITypes } from '../../../primary-record-types';
import { useAuditLog } from '../../../hooks/api/useAuditLog';
import { getIntl } from '../../../utils/intl';
import getDateString from '../../../utils/getDateString';
import Alert from '../../v2BaseComponents/Alert';
import DetailsLayout from '../../v2BaseComponents/DetailsLayout';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import ReferenceCards from './ReferenceCards';
import CommentList from '../../shared/CommentList/CommentList';
import { CommentItem } from '../../shared/CommentList';
import urls from '../../../utils/urls';
import TitledSection from '../../v2BaseComponents/TitledSection';
import ProfileMessageDetails from './ProfileMessageDetails';
import { useLoadedUser } from '../../../hooks/api/useUser';
import { Action } from '../../v2BaseComponents/ActionButtonLayout/types';
import { ActionButtonLayout } from '../../v2BaseComponents/ActionButtonLayout';

const intl = getIntl('screens.mainApp.activity.auditLogDetails', {
    timeStamp: 'Time: ',
    medicalProfile: 'Medical Profile',
    collaborator: 'Collaborator',
    note: 'Note',
    endpoint: 'Connected Health System',
    deletedEndpoint: 'Deleted Health System',
    invitation: 'Invitation',
    careTeamMember: 'Care Team Member',
    partiallyInvalidHeader: 'Activity Is Missing Some Details',
    partiallyInvalidBody:
        'This activity is missing some details.  Some of the details may have been deleted or are no longer available and are not viewable.',
    deleteAuditLog: 'Delete Activity Details',
    deleteNotificationHeader: 'Are you sure you want to delete these activity details? This cannot be undone.',
    deleteButton: 'Yes, delete',
    cancelButton: 'No, cancel',
    sharedReport: 'Shared Report',
    unauthorized: 'Unauthorized',
    unauthorizedContent:
        'You are not authorized to view this content.  You may have lost access to details of this activity since it has been created.',
    references: 'Activity Details References',
    comments: 'Comments',
    deleteMessage: 'Delete Message',
});

export type AuditLogDetailsOwnProps = {
    auditLogId: string;
    profileId: string;
};

export type AuditLogDetailsProps = AuditLogDetailsOwnProps;

export type UseDeleteAuditLogActionButtonArgs = {
    auditLogId: string;
    medicalProfileFileId: string;
    onDeleted: () => void;
};

export type UseDeleteAuditLogActionButtonReturnValue = {
    deleteAuditLogAction: Action;
};

export const useDeleteAuditLogActionButton = ({
    auditLogId,
    medicalProfileFileId,
    onDeleted,
}: UseDeleteAuditLogActionButtonArgs): UseDeleteAuditLogActionButtonReturnValue => {
    const { user } = useLoadedUser({});
    const { isLoadingAuditLog, auditLog, deleteAuditLog, isDeletingAuditLog } = useAuditLog({
        auditLogId: auditLogId,
        medicalProfileId: medicalProfileFileId,
        onDeleteAuditLogSuccess: onDeleted,
    });

    const canDelete =
        !isLoadingAuditLog &&
        !!auditLog &&
        auditLog.status === 'valid' &&
        auditLog.auditLog.type === 'profile-message' &&
        auditLog.auditLog.userId === user.userId;

    const actionButtonProps: Action = {
        title: intl('deleteMessage'),
        onPress: () => {
            deleteAuditLog();
        },
        variant: 'alert',
        isDisabled: isDeletingAuditLog,
        show: canDelete,
        icon: 'Trash2',
        testID: 'delete-audit-log-button',
    };

    return {
        deleteAuditLogAction: actionButtonProps,
    };
};

const AuditLogDetails: React.FC<AuditLogDetailsProps> = ({ auditLogId, profileId }) => {
    const { isLoadingAuditLog, auditLog, comment, isCommenting } = useAuditLog({
        auditLogId,
        medicalProfileId: profileId,
    });

    const renderContent = () => {
        if (!auditLog || auditLog.status === 'unauthorized')
            return (
                <MainContentContainer>
                    <Alert type="error" title={intl('unauthorized')} message={intl('unauthorizedContent')} />
                </MainContentContainer>
            );

        const referenceAuditLog = auditLog.status === 'partially-invalid' ? auditLog.newAuditLog : auditLog.auditLog;

        const renderInvalidAlert = () => (
            <Alert type="info" title={intl('partiallyInvalidHeader')} message={intl('partiallyInvalidBody')} />
        );

        const renderContent = () => {
            if (auditLog.status === 'valid' && auditLog.auditLog.type === 'profile-message') {
                return <ProfileMessageDetails auditLog={auditLog.auditLog} />;
            } else {
                return (
                    <>
                        <Stack vertical space={1}>
                            <Text variant="body-1">{auditLog.auditLogTitle}</Text>
                            <Stack horizontal fullWidth justify="flex-start">
                                <Text variant="body-2">{intl('timeStamp')}</Text>
                                <Text variant="body-2">
                                    {getDateString(new Date(referenceAuditLog.timestamp).toISOString(), 'date-time')}
                                </Text>
                            </Stack>
                        </Stack>
                        <TitledSection title={intl('references')}>
                            {
                                // show an alert if the audit log is invalid
                                auditLog.status === 'partially-invalid' ? renderInvalidAlert() : null
                            }
                            <ReferenceCards auditLog={referenceAuditLog} profileId={profileId} />
                        </TitledSection>
                    </>
                );
            }
        };

        return (
            <DetailsLayout>
                <MainContentContainer testID="activity-details-screen">
                    <Stack vertical fullWidth space={contentStackSpacing}>
                        {renderContent()}
                        <TitledSection title={intl('comments')}>
                            <CommentList
                                disableNoCommentsText
                                maxCommentLength={APITypes.auditLogService.MAX_AUDIT_LOG_COMMENT_LENGTH}
                                comments={auditLog.comments}
                                getCommentId={(comment) => comment.date}
                                renderComment={(comment) =>
                                    comment.status === 'user-deleted' ? null : (
                                        <CommentItem
                                            comment={comment.comment}
                                            firstName={comment.userFirstName}
                                            lastName={comment.userLastName}
                                            preferredName={comment.userPreferredName}
                                            createdAt={comment.date}
                                            avatarAPIUrl={urls.api.auditLogs.auditLogCommentAvatar(
                                                profileId,
                                                auditLogId,
                                                comment.userId,
                                            )}
                                        />
                                    )
                                }
                                isSubmittingComment={isCommenting}
                                onCommentSubmit={(text) => {
                                    comment(text);
                                }}
                            />
                        </TitledSection>
                    </Stack>
                </MainContentContainer>
            </DetailsLayout>
        );
    };

    return (
        <AnimateLoadedContent isLoading={isLoadingAuditLog || !auditLog}>
            {() => <ActionButtonLayout scrollable>{renderContent()}</ActionButtonLayout>}
        </AnimateLoadedContent>
    );
};

export default React.memo(AuditLogDetails);
